/*
  card-slider.js — shared scroll-snap slider engine.

  Discovers every [data-card-slider] on the page and wires up arrow nav, dot
  pagination, optional infinite-loop cloning, and an optional max-width gate so
  responsive sections (e.g. multicolumn) can run the slider on mobile only.

  Data attribute contract (consumer-set on the rendered HTML):

    [data-card-slider]                     — root wrapper (required)
    [data-card-slider-track]               — overflow-x scroll container (required)
    [data-card-slider-card]                — each card (required)
    [data-card-slider-prev]                — prev arrow button (optional)
    [data-card-slider-next]                — next arrow button (optional)
    [data-card-slider-dots]                — dots container (optional, populated by JS)

  Root attributes (optional, with defaults):

    data-card-slider-infinite-loop="true"  — clone + middle-scroll, default true
    data-card-slider-max-width="768"       — only init when window.innerWidth <= this
    data-card-slider-step="card"           — arrow scroll step: "card" (default) or "viewport"
    data-card-slider-end-runway="false"    — opt out of the finite + start-snap end-runway
                                             (default true). Disable on multi-up carousels
                                             where the desired stop is "last card enters view"
                                             rather than "last card snaps to start"

  Dots container attribute (optional):

    data-card-slider-dot-class             — class applied to each generated dot
                                             (default: "card-slider__dot"; "--active" suffix
                                             is appended for the active dot)

  Behaviour:

  • Infinite loop mode (default) clones all real cards before and after the
    real set and pre-scrolls to the middle. On scrollend the JS jumps back to
    the corresponding real card position so the illusion never breaks.
  • Finite mode disables prev/next at the track's start/end via the HTML
    `disabled` attribute; consumer CSS styles `:disabled` as needed.
  • Arrow clicks scroll by one card-width + gap and let the CSS scroll-snap
    engine resolve the final position — works equally well for centre-snap and
    start-snap consumers.
  • max-width gate: when set, the engine initialises only while
    window.innerWidth <= the value and tears down (removing clones, listeners,
    and dots) once the viewport crosses above it. Re-initialises on the way
    back down.
  • `data-card-slider-init="true"` is set on the root after first init to
    guard against double-binding on Shopify theme editor section reloads.
*/
(function () {
  'use strict';

  /* Self-dedupe guard. The engine is loaded per-section (see snippet headers
     for the <script src> tags) rather than globally in theme.liquid, so a
     page with multiple slider-consuming sections will have the script tag
     repeated. The browser fetches once via cache, but executes per tag.
     This flag short-circuits subsequent runs so functions, event listeners,
     and bytecode side-effects all settle exactly once. */
  if (window.__cardSliderInit) return;
  window.__cardSliderInit = true;

  function getDotClass(dotsEl) {
    return (dotsEl && dotsEl.getAttribute('data-card-slider-dot-class')) || 'card-slider__dot';
  }

  function clearDots(dotsEl) {
    if (dotsEl) dotsEl.innerHTML = '';
  }

  function getSnapAlign(card) {
    /* Reads the resolved scroll-snap-align value from the card itself.
       Returns 'center' | 'start' | 'end' | 'none'. The first axis applies
       to inline (horizontal) for our scroll-snap-type: x setup. */
    if (!card) return 'center';
    var raw = window.getComputedStyle(card).scrollSnapAlign || '';
    /* `scroll-snap-align` can be one or two values (block, inline). With
       scroll-snap-type: x mandatory, the inline axis is what we care about.
       Browsers normalise single-value to "both axes" so the first token
       applies to inline too. */
    var token = raw.split(' ')[0].trim().toLowerCase();
    if (token === 'start' || token === 'end' || token === 'none') return token;
    return 'center';
  }

  function snapPos(track, item) {
    /* Returns the scrollLeft value that lands `item` at its CSS snap
       position. Reads scroll-snap-align on the card and scroll-padding
       on the track so the math matches whatever the consumer set.

       Uses getBoundingClientRect (viewport-relative, sub-pixel-precise)
       rather than offsetLeft. The reason matters:

         • offsetLeft is measured relative to the element's offsetParent.
           When no ancestor between the card and the body has
           `position: relative` (or another positioning context), the
           offsetParent walks all the way up to `.shopify-section`, and
           the math only accidentally works because the carousel
           wrapper bleeds to viewport-left via `width: 100vw +
           margin-left: calc(50% - 50vw)` — so both card.offsetLeft and
           track.offsetLeft happen to share the same anchor at 0.
         • Any sub-pixel drift in that bleed calc (which can happen on
           every real mobile browser — Chrome on Android, Safari, etc.
           — when the inner's padding-inline doesn't divide evenly into
           the viewport width) feeds straight into the snap position,
           shifting the active card off-centre.

       getBoundingClientRect bypasses offsetParent entirely. We compute
       the card's position relative to the track by subtracting their
       rects in viewport coords, then add the current scrollLeft to get
       a stable scroll-content-relative offset that doesn't depend on
       how the track is positioned on the page.

       Math.round at the end keeps the returned value an integer so the
       browser's scroll engine and the CSS snap engine don't disagree
       by a half-pixel rounding difference. */
    var trackRect = track.getBoundingClientRect();
    var itemRect = item.getBoundingClientRect();
    /* (itemRect.left - trackRect.left) is the card's left edge offset
       from the track's left edge in viewport coords AT THE CURRENT
       SCROLL POSITION. Adding scrollLeft converts that to the card's
       offset relative to the track's scroll-content (0-anchor at
       track-content-left), which is independent of the current scroll. */
    var itemInTrack = (itemRect.left - trackRect.left) + track.scrollLeft;
    var itemWidth = itemRect.width;
    var trackWidth = trackRect.width;

    var align = getSnapAlign(item);
    var pos;
    if (align === 'start') {
      var padStart = parseFloat(window.getComputedStyle(track).scrollPaddingInlineStart) || 0;
      pos = itemInTrack - padStart;
    } else if (align === 'end') {
      var padEnd = parseFloat(window.getComputedStyle(track).scrollPaddingInlineEnd) || 0;
      pos = itemInTrack + itemWidth - trackWidth + padEnd;
    } else {
      /* 'center' (and 'none' as a sensible default) */
      pos = itemInTrack + itemWidth / 2 - trackWidth / 2;
    }
    return Math.round(pos);
  }

  function getStep(track, card) {
    if (!card) return 0;
    var cs = window.getComputedStyle(track);
    var gap = parseFloat(cs.columnGap || cs.gap) || 0;
    return card.offsetWidth + gap;
  }

  function initSlider(root) {
    /* Idempotency: skip if this root is already initialised and currently
       considered active (i.e. clones / listeners are in place). The boot
       phase clears _cardSliderInstance during destroy so re-init can happen. */
    if (root._cardSliderInstance) return;

    var track = root.querySelector('[data-card-slider-track]');
    if (!track) return;
    var realCards = Array.from(track.querySelectorAll('[data-card-slider-card]'));
    if (realCards.length < 2) return;

    var infiniteLoop = root.getAttribute('data-card-slider-infinite-loop') !== 'false';
    var endRunway = root.getAttribute('data-card-slider-end-runway') !== 'false';
    var prevBtn = root.querySelector('[data-card-slider-prev]');
    var nextBtn = root.querySelector('[data-card-slider-next]');
    var dotsEl = root.querySelector('[data-card-slider-dots]');
    var dotClass = getDotClass(dotsEl);
    var dotActiveClass = dotClass + '--active';
    var stepMode = root.getAttribute('data-card-slider-step') || 'card';

    var allItems = null;
    var clones = [];
    var isJumping = false;
    var scrollTimer = null;
    var arrowHandlers = { prev: null, next: null };
    var scrollListener = null;
    var scrollEndListener = null;

    /* ── Shared helpers ─────────────────────────────────────────────────── */

    function jumpTo(index) {
      isJumping = true;
      track.style.scrollSnapType = 'none';
      track.scrollLeft = snapPos(track, allItems[index]);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          track.style.scrollSnapType = '';
          isJumping = false;
        });
      });
    }

    function getClosestIndex() {
      var sl = track.scrollLeft;
      var closest = 0, dist = Infinity;
      allItems.forEach(function (item, i) {
        var d = Math.abs(snapPos(track, item) - sl);
        if (d < dist) { dist = d; closest = i; }
      });
      return closest;
    }

    function updateDots(activeRealIndex) {
      if (!dotsEl) return;
      var dots = dotsEl.querySelectorAll('.' + dotClass);
      dots.forEach(function (dot, i) {
        dot.classList.toggle(dotActiveClass, i === activeRealIndex);
      });
    }

    function buildDots(onClickIndex) {
      if (!dotsEl) return;
      clearDots(dotsEl);
      realCards.forEach(function (_, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = dotClass + (i === 0 ? ' ' + dotActiveClass : '');
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener('click', function () { onClickIndex(i); });
        dotsEl.appendChild(dot);
      });
    }

    /* ── Infinite-loop mode ─────────────────────────────────────────────── */

    function handleScrollEndInfinite() {
      if (isJumping) return;
      clearTimeout(scrollTimer);
      var n = realCards.length;
      var closest = getClosestIndex();
      if (closest < n) {
        /* User landed in the leading clone band. allItems[closest] is a clone
           of real card `closest`, so the post-jump dot should highlight
           index `closest`. Update dots BEFORE jumpTo so the indicator never
           shows a stale state during the instant scrollLeft jump. */
        updateDots(closest);
        jumpTo(closest + n);
      } else if (closest >= 2 * n) {
        /* Symmetric trailing-clone case: allItems[closest] is a clone of
           real card `closest - 2n`. Without this update, after swiping past
           the last real card the dot indicator stays stuck on the last real
           index while the engine wraps the user back to a different real
           card — which manifests as "dot navigation goes buggy after a
           manual swipe." */
        updateDots(closest - 2 * n);
        jumpTo(closest - n);
      } else {
        updateDots(closest - n);
      }
    }

    function onScrollInfinite() {
      if (isJumping) return;
      /* Live dot tracking — only when the scroll position lands in the
         middle (real-card) band. Skipping the leading / trailing clone
         bands avoids dot flicker mid-swipe before the wrap-jump fires;
         handleScrollEndInfinite is responsible for the dot update that
         pairs with each wrap-jump.
         getClosestIndex is O(3n) — cheap for typical slider sizes —
         but we still gate it behind the band check above scrollend
         settle timing, so on iOS where momentum scroll fires `scroll`
         dozens of times per second the user sees dot updates track
         their finger in real time rather than only after settle. */
      var n = realCards.length;
      var closestLive = getClosestIndex();
      if (closestLive >= n && closestLive < 2 * n) {
        updateDots(closestLive - n);
      }
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(handleScrollEndInfinite, 150);
    }

    function initInfinite() {
      var n = realCards.length;
      clones = [];

      /* Prepend a reverse-order copy so the visual order matches when scrolling
         backward into the prefix. */
      for (var i = n - 1; i >= 0; i--) {
        var pre = realCards[i].cloneNode(true);
        pre.setAttribute('aria-hidden', 'true');
        pre.setAttribute('tabindex', '-1');
        track.insertBefore(pre, track.firstChild);
        clones.push(pre);
      }

      /* Append a forward-order copy for the suffix. */
      for (var j = 0; j < n; j++) {
        var post = realCards[j].cloneNode(true);
        post.setAttribute('aria-hidden', 'true');
        post.setAttribute('tabindex', '-1');
        track.appendChild(post);
        clones.push(post);
      }

      allItems = Array.from(track.querySelectorAll('[data-card-slider-card]'));

      /* Start at the middle real card so equal slivers show on each side at init.
         Disable scroll-snap-type around the position set so iOS Safari doesn't
         re-snap to a sub-pixel-different target (with `mandatory` snap +
         sub-pixel rounding, the browser may snap to a target whose computed
         position is 1px off from `snapPos`, leaving the first-paint card
         visually shifted). Same RAF-chain pattern jumpTo uses. */
      var midIndex = Math.floor(n / 2);
      track.style.scrollSnapType = 'none';
      track.scrollLeft = snapPos(track, allItems[n + midIndex]);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          track.style.scrollSnapType = '';
        });
      });

      buildDots(function (i) {
        track.scrollTo({ left: snapPos(track, allItems[i + n]), behavior: 'smooth' });
      });

      scrollListener = onScrollInfinite;
      scrollEndListener = handleScrollEndInfinite;
      track.addEventListener('scroll', scrollListener, { passive: true });
      track.addEventListener('scrollend', scrollEndListener);
    }

    /* ── Finite mode ────────────────────────────────────────────────────── */

    function updateArrowDisabled() {
      if (!prevBtn && !nextBtn) return;
      var atStart = track.scrollLeft <= 1;
      var atEnd = (track.scrollLeft + track.clientWidth) >= track.scrollWidth - 1;
      if (prevBtn) prevBtn.toggleAttribute('disabled', atStart);
      if (nextBtn) nextBtn.toggleAttribute('disabled', atEnd);
    }

    function onScrollFinite() {
      updateArrowDisabled();
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        if (!dotsEl) return;
        var sl = track.scrollLeft;
        var closest = 0, dist = Infinity;
        realCards.forEach(function (item, i) {
          var d = Math.abs(item.offsetLeft - sl);
          if (d < dist) { dist = d; closest = i; }
        });
        updateDots(closest);
      }, 150);
    }

    function initFinite() {
      allItems = realCards;
      track.scrollLeft = 0;
      buildDots(function (i) {
        track.scrollTo({ left: snapPos(track, realCards[i]), behavior: 'smooth' });
      });
      scrollListener = onScrollFinite;
      track.addEventListener('scroll', scrollListener, { passive: true });
      updateArrowDisabled();
      applyEndCardPadding();
    }

    /* applyEndCardPadding — finite + start-snap helper.

       Without this, the browser caps scrollLeft at `scrollWidth − clientWidth`,
       so a finite start-snap slider's LAST card can never reach the leftmost
       snap position — there isn't enough scrollable runway past it. Clicking
       its dot leaves the card stuck mid-viewport and the engine's snap target
       quietly clamps.

       The fix is to extend the runway so each card can snap to the start.
       Derivation:
         maxScrollLeft (target for last card)  =  lastCard.offsetLeft − padStart
         maxScrollLeft                          =  scrollWidth − clientWidth
         scrollWidth                            =  lastCard.offsetLeft + lastCard.width + runway
       Solving:
         runway = clientWidth − lastCard.width − padStart

       Applied as `margin-inline-end` on the LAST card rather than
       `padding-right` on the track. Padding shrinks the track's content-box,
       which feeds percentage-based flex-basis calculations and can drive a
       runaway feedback loop in finite mode: e.g. multicolumn-carousel
       desktop cards use `flex: 0 0 calc(25% - 15px)` against the track's
       content-box width — applying padding-right shrinks that width, the
       cards re-flex narrower, lastCard.offsetWidth drops, the helper runs
       again on the next layout pass with a now-larger required runway, and
       within a couple of cycles the cards collapse to 0px. A flex child's
       margin contributes to scrollWidth but does NOT enter the parent's
       content-box used for percentage flex-basis, so the layout is stable.

       Cleared in destroy() so re-init starts clean.

       Skipped for infinite mode (clones already provide runway in both
       directions), centre-snap (centre math doesn't push the last card to
       one edge — natural maxScrollLeft is already enough), and any root
       that opts out via data-card-slider-end-runway="false" (multi-up
       carousels where the desired stop is "last card enters view" rather
       than "last card snaps to start"). */
    function applyEndCardPadding() {
      if (infiniteLoop) return;
      if (!endRunway) return;
      if (realCards.length < 2) return;
      if (getSnapAlign(realCards[0]) !== 'start') return;

      var lastCard = realCards[realCards.length - 1];
      var padStart = parseFloat(window.getComputedStyle(track).scrollPaddingInlineStart) || 0;
      var requiredRunway = track.clientWidth - lastCard.offsetWidth - padStart;
      if (requiredRunway <= 0) {
        lastCard.style.marginInlineEnd = '';
        return;
      }

      lastCard.style.marginInlineEnd = requiredRunway + 'px';
    }

    /* ── Arrows ─────────────────────────────────────────────────────────── */

    function attachArrows() {
      if (prevBtn) {
        arrowHandlers.prev = function () {
          var step = stepMode === 'viewport' ? track.clientWidth : getStep(track, realCards[0]);
          track.scrollBy({ left: -step, behavior: 'smooth' });
        };
        prevBtn.addEventListener('click', arrowHandlers.prev);
      }
      if (nextBtn) {
        arrowHandlers.next = function () {
          var step = stepMode === 'viewport' ? track.clientWidth : getStep(track, realCards[0]);
          track.scrollBy({ left: step, behavior: 'smooth' });
        };
        nextBtn.addEventListener('click', arrowHandlers.next);
      }
    }

    /* ── Boot ───────────────────────────────────────────────────────────── */

    attachArrows();
    if (infiniteLoop) {
      initInfinite();
    } else {
      initFinite();
    }

    /* Stash the teardown + resize hook on the root so the outer responsive
       loop can invoke them without needing closure access. */
    root._cardSliderInstance = {
      destroy: function () {
        clearTimeout(scrollTimer);
        if (scrollListener) track.removeEventListener('scroll', scrollListener);
        if (scrollEndListener) track.removeEventListener('scrollend', scrollEndListener);
        if (prevBtn && arrowHandlers.prev) prevBtn.removeEventListener('click', arrowHandlers.prev);
        if (nextBtn && arrowHandlers.next) nextBtn.removeEventListener('click', arrowHandlers.next);
        if (prevBtn) prevBtn.removeAttribute('disabled');
        if (nextBtn) nextBtn.removeAttribute('disabled');
        clones.forEach(function (c) { c.remove(); });
        clones = [];
        if (realCards.length) realCards[realCards.length - 1].style.marginInlineEnd = '';
        allItems = null;
        clearDots(dotsEl);
        track.style.scrollSnapType = '';
        track.scrollLeft = 0;
        root._cardSliderInstance = null;
      },
      /* Called from applyResponsive on every resize so viewport-dependent
         state tracks viewport changes:
           • End-card padding (finite start-snap mode): depends on
             track.clientWidth.
           • Active card recentre: snapPos() reads trackRect.width to
             compute the scrollLeft that lands the active card at its
             CSS snap position. When the viewport widens (e.g. rotating
             an iPhone Pro Max from portrait 430 to landscape 932, or
             dragging dev-tools' responsive viewport), trackRect.width
             grows but the engine's stored scrollLeft anchor stays at
             its old value, leaving the active card shifted off-centre.
             Recomputing snapPos for the currently active card (the one
             closest to the existing scrollLeft) and re-assigning gives
             us the correct anchor for the new viewport width without
             changing which card is visually active. */
      onResize: function () {
        applyEndCardPadding();
        if (!allItems) return;
        var current = getClosestIndex();
        if (current < 0 || current >= allItems.length) return;
        var newScrollLeft = snapPos(track, allItems[current]);
        /* Skip if already aligned (avoids a redundant scroll event that
           would trigger onScrollInfinite -> live dot update -> handler). */
        if (Math.abs(track.scrollLeft - newScrollLeft) <= 0.5) return;
        track.scrollLeft = newScrollLeft;
      }
    };
  }

  function destroySlider(root) {
    if (root._cardSliderInstance) root._cardSliderInstance.destroy();
  }

  /* ── Responsive gate manager ─────────────────────────────────────────── */

  function applyResponsive(root) {
    var maxWidth = parseInt(root.getAttribute('data-card-slider-max-width'), 10);
    if (isNaN(maxWidth)) {
      /* No gate — always init. */
      initSlider(root);
    } else {
      var shouldBeActive = window.innerWidth <= maxWidth;
      var isActive = !!root._cardSliderInstance;
      if (shouldBeActive && !isActive) initSlider(root);
      else if (!shouldBeActive && isActive) destroySlider(root);
    }
    /* Re-apply viewport-dependent padding for active instances after layout
       has settled. End-card padding depends on clientWidth, which changes
       with viewport, so we need to recompute on resize. */
    var instance = root._cardSliderInstance;
    if (instance && typeof instance.onResize === 'function') instance.onResize();
  }

  function bootResponsive(root) {
    applyResponsive(root);
    if (root.dataset.cardSliderInit === 'true') return;
    root.dataset.cardSliderInit = 'true';

    /* Resize handler — split into two timing tiers so the active card tracks
       the viewport width smoothly during a continuous drag-resize:

         • IMMEDIATE: `instance.onResize()` runs synchronously on every
           resize event. This recomputes snapPos for the currently active
           card against the new trackRect.width and reassigns scrollLeft,
           so the card visually slides as the viewport widens (e.g. the
           iPhone Pro Max 430 → landscape transition the user reported,
           or dev-tools responsive-mode drag). Without this, the active
           card sits at the stale scrollLeft anchor — increasingly
           off-centre as the gap grows — until the user stops dragging.
           getClosestIndex + snapPos are both O(n) and cheap; modern
           browsers also rate-limit resize events to ~display-refresh-rate.

         • DEBOUNCED 100ms: full `applyResponsive(root)` runs after the
           drag settles. This is the expensive path that may init or
           destroy the slider (clones + listeners) when the viewport
           crosses the `data-card-slider-max-width` gate, and re-runs
           applyEndCardPadding. Debouncing avoids thrashing this through
           every intermediate width during the drag. */
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      var instance = root._cardSliderInstance;
      if (instance && typeof instance.onResize === 'function') {
        instance.onResize();
      }
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { applyResponsive(root); }, 100);
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-card-slider]').forEach(bootResponsive);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Re-run on Shopify theme editor section reload. */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
